import React from 'react';
import {
  Link,
  Copy,
  Share2,
  RefreshCw,
  Download,
  Lock
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';

interface PublicURLSectionProps {
  publicSlug: string;
  profileUrl: string;
  copiedToast: boolean;
  canRegenerateLink: boolean;
  isPublic: boolean;
  profileStatus: string;
  onCopyLink: () => void;
  onShare: () => void;
  onRegenerateLink: () => void;
  onDownloadVCard: () => void;
  onPublicToggle: (value: boolean) => void;
  t: (key: string) => string;
}

const PublicURLSection: React.FC<PublicURLSectionProps> = ({
  publicSlug,
  profileUrl,
  copiedToast,
  canRegenerateLink,
  isPublic,
  profileStatus,
  onCopyLink,
  onShare,
  onRegenerateLink,
  onDownloadVCard,
  onPublicToggle,
  t
}) => {
  if (!publicSlug) return null;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground flex items-center">
            <Link className="w-5 h-5 me-2" />
            {t('Public Profile URL')}
          </h2>

          {/* Make Profile Public Toggle */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">{t('Make Profile Public')}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic || profileStatus === 'pendingapproval'}
                onChange={(e) => onPublicToggle(e.target.checked)}
                disabled={profileStatus === 'pendingapproval'}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus-visible:outline-none peer-focus-visible:ring-4 peer-focus-visible:ring-ring rounded-full peer peer-checked:after:ltr:translate-x-full peer-checked:after:rtl:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:ltr:left-[2px] after:rtl:right-[2px] after:rtl:left-auto after:bg-white after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
            </label>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-muted rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-2">{t('Your public profile link:', { nsSeparator: false })}</p>
            <p className="text-sm font-mono text-foreground break-all">{profileUrl}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onCopyLink}
              className="gap-2"
            >
              <Copy className="w-4 h-4" />
              {copiedToast ? t('Copied!') : t('Copy Link')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              {t('Share')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onRegenerateLink}
              disabled={!canRegenerateLink}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t('Regenerate Link')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onDownloadVCard}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {t('Download vCard')}
            </Button>
          </div>

          {!canRegenerateLink && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {t('Link regeneration is controlled by company policy')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PublicURLSection;
